// NOTE: A tracking system such as Sentry should replace the console

export const LogLevel = {
	NONE: "NONE",
	ERROR: "ERROR",
	WARN: "WARN",
	DEBUG: "DEBUG",
	ALL: "ALL",
} as const;

export type LogLevelType = (typeof LogLevel)[keyof typeof LogLevel];

const LogLevelNumber: Record<LogLevelType, number> = {
	NONE: 0,
	ERROR: 1,
	WARN: 2,
	DEBUG: 3,
	ALL: 4,
};

export interface LoggerConfig {
	prefix?: string;
	level?: LogLevelType;
	showLevel?: boolean;
}

export class Logger {
	prefix: string;
	level: LogLevelType;
	showLevel: boolean;
	levelNumber: number;

	constructor({
		prefix = "",
		level = LogLevel.ALL,
		showLevel = true,
	}: LoggerConfig = {}) {
		this.prefix = prefix;
		this.level = level;
		this.levelNumber = LogLevelNumber[this.level];
		this.showLevel = showLevel;
	}

	debug = (...args: any[]): void => {
		if (this.canWrite(LogLevel.DEBUG)) {
			this.write(LogLevel.DEBUG, ...args);
		}
	};

	warn = (...args: any[]): void => {
		if (this.canWrite(LogLevel.WARN)) {
			this.write(LogLevel.WARN, ...args);
		}
	};

	error = (...args: any[]): void => {
		if (this.canWrite(LogLevel.ERROR)) {
			this.write(LogLevel.ERROR, ...args);
		}
	};

	canWrite(level: LogLevelType): boolean {
		return this.levelNumber >= LogLevelNumber[level];
	}

	write(level: LogLevelType, ...args: any[]): void {
		let prefix = this.prefix;

		if (this.showLevel) {
			prefix = `- ${level} ${prefix}`;
		}

		if (level === LogLevel.ERROR) {
			console.error(prefix, ...args);
		} else {
			console.log(prefix, ...args);
		}
	}
}

// This can be extended to create context specific logger (Server Action, Router Handler, etc.)
// to add context information (IP, User-Agent, timestamp, etc.)

export function createLogger(config: LoggerConfig = {}): Logger {
	return new Logger(config);
}
